import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { isStaff, refreshSession, useAuth } from "../lib/auth";
import Forbidden from "../pages/Forbidden";
import Skeleton from "./states/Skeleton";

/**
 * MER-193: gate for the `/admin/*` route tree. Mirrors `AuthGate`
 * (redirects signed-out users to /login, preserving the original
 * target) and additionally checks the `is_staff` JWT claim. Signed-in
 * non-staff users see the Forbidden page so the admin URL surface
 * does not leak the existence of pages they cannot reach.
 *
 * MER-193 followup: when the session is signed-in but the in-memory
 * JWT lacks `is_staff`, force one refreshSession() round trip before
 * showing 403. A freshly-granted staff user otherwise sees Forbidden
 * even though the database flag is set, because the cached access
 * token was issued before the grant SQL ran. `onAuthStateChange`
 * picks up the refreshed session and re-renders this gate.
 */
export default function StaffGate(props: { children: ReactNode }) {
  const { session, isLoading } = useAuth();
  const location = useLocation();
  const [refreshState, setRefreshState] = useState<"idle" | "refreshing" | "done">("idle");

  useEffect(() => {
    if (isLoading) return;
    if (!session) return;
    if (isStaff(session)) return;
    if (refreshState !== "idle") return;
    setRefreshState("refreshing");
    // Fire-and-forget. The auth state subscription in useAuth picks up
    // the refreshed session and re-renders; we just mark the attempt
    // done so the gate doesn't refresh in a loop.
    void refreshSession().finally(() => setRefreshState("done"));
  }, [isLoading, session, refreshState]);

  if (isLoading || refreshState === "refreshing") {
    return (
      <main style={{ padding: 32, display: "grid", placeItems: "center", minHeight: "60vh" }}>
        <Skeleton label="Checking session" height={80} />
      </main>
    );
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (!isStaff(session)) {
    return <Forbidden />;
  }

  return <>{props.children}</>;
}
